import type { Mutable } from 'utility-types'

import { assert } from '../../../utils'

import {
    Capability,
    ConnectwareError,
    ConnectwareErrorType,
    isAuthenticatingWithOtp,
    type MfaActivationData,
    type MfaUpdateCredentials,
    Translation,
    type ValidOtp,
} from '../../../domain'

import type { MfaService, TranslationService } from '../../../application'

import { type ConnectwareHTTPServiceOptions, FetchConnectwareHTTPService } from '../Base'
import { mapBackupCodeBodyRequest, mapOtpBodyRequest } from './mappers'

export class ConnectwareHTTPMfaService extends FetchConnectwareHTTPService implements MfaService {
    private readonly errorHandlers = {
        500: () => new ConnectwareError(ConnectwareErrorType.SERVER_ERROR, this.translationService.translate(Translation.SERVER_ERROR)),
        400: () => new ConnectwareError(ConnectwareErrorType.BAD_REQUEST, 'Unexpected bad request error detected'),
    } as const

    constructor (options: ConnectwareHTTPServiceOptions, private readonly translationService: TranslationService) {
        super(options)
    }

    private mapInvalidCredentials (credentials: MfaUpdateCredentials): ConnectwareError {
        return new ConnectwareError(
            ConnectwareErrorType.AUTHENTICATION,
            this.translationService.translate(Translation.WRONG_MFA_CREDENTIALS, { type: isAuthenticatingWithOtp(credentials) ? 'otp' : 'backupCode' })
        )
    }

    isEnabled (): Promise<boolean> {
        return this.request<'/api/mfa/isenrolled', 'GET', boolean>({
            capability: Capability.USE_MFA,
            method: 'GET',
            path: '/api/mfa/isenrolled',
            authenticate: true,
            handlers: {
                ...this.errorHandlers,
                200: (response) => response.getJson().then(({ isEnrolled }) => isEnrolled),
            },
        })
    }

    enable (): Promise<MfaActivationData> {
        return this.request<'/api/mfa/enable', 'POST', MfaActivationData>({
            capability: Capability.USE_MFA,
            method: 'POST',
            path: '/api/mfa/enable',
            authenticate: true,
            handlers: {
                ...this.errorHandlers,
                200: async (response) => {
                    const { uri } = await response.getJson()

                    const url = new URL(uri)
                    const secret = url.searchParams.get('secret')
                    assert(secret, new ConnectwareError(ConnectwareErrorType.MAPPING_ERROR, 'Could not find secret in uri', { uri }))

                    return { uri: url, secret }
                },
                409: () =>
                    new ConnectwareError(
                        ConnectwareErrorType.GENERAL_BUSINESS_RULE_INFRACTION,
                        this.translationService.translate(Translation.MFA_ALREADY_ENABLED_ERROR)
                    ),
            },
        })
    }

    disable (credentials: MfaUpdateCredentials): Promise<void> {
        return this.request<'/api/mfa/disable', 'POST', void>({
            capability: Capability.USE_MFA,
            method: 'POST',
            path: '/api/mfa/disable',
            authenticate: true,
            body: isAuthenticatingWithOtp(credentials) ? mapOtpBodyRequest(credentials.otp) : mapBackupCodeBodyRequest(credentials.backupCode),
            handlers: { ...this.errorHandlers, 401: () => this.mapInvalidCredentials(credentials), 200: () => Promise.resolve() },
        })
    }

    confirmEnabling (otp: ValidOtp): Promise<string[]> {
        return this.request<'/api/mfa/validate', 'POST', string[]>({
            capability: Capability.USE_MFA,
            method: 'POST',
            path: '/api/mfa/validate',
            authenticate: true,
            body: mapOtpBodyRequest(otp),
            handlers: {
                ...this.errorHandlers,
                200: (response) => response.getJson().then(({ backupCodes }) => backupCodes as Mutable<typeof backupCodes>),
                401: () => this.mapInvalidCredentials({ otp, backupCode: null }),
            },
        })
    }

    regenerateBackupCodes (credentials: MfaUpdateCredentials): Promise<string[]> {
        return this.request<'/api/mfa/regenerate/backupcodes', 'POST', string[]>({
            capability: Capability.USE_MFA,
            method: 'POST',
            path: '/api/mfa/regenerate/backupcodes',
            authenticate: true,
            body: isAuthenticatingWithOtp(credentials) ? mapOtpBodyRequest(credentials.otp) : mapBackupCodeBodyRequest(credentials.backupCode),
            handlers: {
                ...this.errorHandlers,
                401: () => this.mapInvalidCredentials(credentials),
                200: (response) => response.getJson().then(({ backupCodes }) => backupCodes as Mutable<typeof backupCodes>),
            },
        })
    }
}
